<template>
  <main id="main" class="main">
    <section class="section">
      <div class="row">
        <div class="col-lg-12">
          <div class="card worker-card">
            <div class="card-body p-4">
              <div class="row mb-4">
                <div class="col-sm-9">
                  <div class="pagetitle">
                    <h1 class="text-left text-uppercase">Worker Configuration</h1>
                    <nav>
                      <ol class="breadcrumb breadJa">
                        <li class="breadcrumb-item">
                          <router-link :to="{ name: 'home' }">Home</router-link>
                        </li>
                        <router-link :to="{ name: 'worker-config' }" class="breadcrumb-item">Worker Config</router-link>
                      </ol>
                    </nav>
                  </div>
                </div>
                <div class="col-sm-3 d-flex justify-content-end align-items-center">
                  <button 
                    class="btn btn-primary shadow-sm" 
                    @click="openCreateModal"
                    :style="{ backgroundColor: themeColor, borderColor: themeColor }"
                  >
                    <i class="bi bi-plus-circle me-1"></i> Tambah Config
                  </button>
                </div>
              </div>

              <!-- Search and Filters -->
              <div class="row align-items-center mb-4">
                <div class="col-md-6 mb-3 mb-md-0">
                  <div class="d-flex align-items-center">
                    <span class="me-2 text-muted small">Show</span>
                    <select v-model="entriesPerPage" class="form-select form-select-sm w-auto">
                      <option :value="10">10</option>
                      <option :value="20">20</option>
                      <option :value="50">50</option>
                    </select>
                    <span class="ms-2 text-muted small">entries</span>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="d-flex justify-content-md-end">
                    <div class="search-container w-100" style="max-width: 300px;">
                      <i class="bi bi-search search-icon"></i>
                      <input
                        type="text"
                        v-model="searchQuery"
                        class="form-control search-input"
                        placeholder="Search configurations..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Table -->
              <div class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead class="table-primary">
                    <tr style="vertical-align:middle;">
                      <th class="text-center" style="width: 50px;">No</th>
                      <th>Worker Name</th>
                      <th class="text-center">Circle Instances</th>
                      <th class="text-center" style="width: 200px;">Application</th>
                      <th class="text-center">Type</th>
                      <th class="text-center text-nowrap">Capabilities</th>
                      <th class="text-center">Interval</th>
                      <th class="text-center">Status</th>
                      <th class="text-center text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="9" class="text-center py-5">
                        <div class="spinner-border text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <div class="mt-2 text-muted">Fetching configurations...</div>
                      </td>
                    </tr>
                    <tr v-else-if="filteredConfigs.length === 0">
                      <td colspan="9" class="text-center py-5">
                        <div class="text-muted">No worker configurations found.</div>
                      </td>
                    </tr>
                    <tr v-for="(config, index) in paginatedConfigs" :key="config.id" class="fade-in">
                      <td class="text-center">{{ (currentPage - 1) * entriesPerPage + index + 1 }}</td>
                      <td>
                        <div class="fw-bold">{{ config.worker_name }}</div>
                        <div class="small text-muted text-truncate" style="max-width: 150px;">ID: {{ config.id }}</div>
                      </td>
                      <td class="text-center">
                        <span 
                          class="badge rounded-pill" 
                          :style="{ ...getBadgeStyle(config.circle), minWidth: '70px' }"
                        >
                          {{ config.circle }}
                        </span>
                      </td>
                      <td class="text-center" style="max-width: 200px;">
                        <div class="d-flex gap-1 justify-content-center align-items-center flex-wrap">
                          <span 
                            v-for="(app, index) in parseApplications(config.application)" 
                            :key="index"
                            class="badge rounded-pill" 
                            :style="{ ...getBadgeStyle(app), minWidth: '70px' }"
                          >
                            {{ app }}
                          </span>
                        </div>
                      </td>
                      <td class="text-center">
                        <span class="badge rounded-pill" :class="config.message_type === 'direct' ? 'bg-type-direct' : 'bg-type-group'" style="min-width: 70px; font-size: 10px; padding: 6px 10px;">
                          {{ config.message_type }}
                        </span>
                      </td>
                      <td class="text-center text-nowrap">
                        <div class="d-flex gap-1 justify-content-center">
                          <!-- Media State -->
                          <span 
                            class="badge rounded-pill border py-1 px-2" 
                            :class="config.allow_media ? 'bg-info text-white border-info' : 'bg-light text-muted border-secondary border-opacity-25 opacity-50'"
                            style="font-size: 9px; min-width: 45px; letter-spacing: 0.5px;"
                            :data-tooltip="config.allow_media ? 'Media Processing: Enabled' : 'Media Processing: Disabled'"
                          >
                            <i class="bi bi-images me-1"></i>MED
                          </span>

                          <!-- Auto-Cancel Pending State (replace_pending) -->
                          <span 
                            class="badge rounded-pill border py-1 px-2" 
                            :class="config.replace_pending ? 'bg-warning text-dark border-warning' : 'bg-light text-muted border-secondary border-opacity-25 opacity-50'"
                            style="font-size: 9px; min-width: 45px; letter-spacing: 0.5px;"
                            :data-tooltip="config.replace_pending ? 'Auto-Cancel Pending: Enabled' : 'Auto-Cancel Pending: Disabled'"
                          >
                            <i class="bi bi-arrow-counterclockwise me-1"></i>RPL
                          </span>
                          
                          <!-- Webhook State -->
                          <span 
                            class="badge rounded-pill border py-1 px-2" 
                            :class="isWebhookActive(config) ? 'bg-success text-white border-success' : 'bg-light text-muted border-secondary border-opacity-25 opacity-50'"
                            style="font-size: 9px; min-width: 45px; letter-spacing: 0.5px;"
                            :data-tooltip="isWebhookActive(config) ? 'Webhook Callback: Active' : 'Webhook Callback: Inactive'"
                          >
                            <i class="bi bi-link-45deg me-1"></i>WH
                          </span>
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="d-flex gap-1 justify-content-center align-items-center flex-nowrap">
                          <span class="badge rounded-pill bg-primary bg-opacity-10 text-primary fw-bold" style="font-size: 10px; min-width: 70px; padding: 6px 8px;">
                            Min: {{ formatInterval(config.interval_seconds) }}
                          </span>
                          <span class="badge rounded-pill bg-secondary bg-opacity-10 text-secondary fw-bold" style="font-size: 10px; min-width: 70px; padding: 6px 8px;">
                            Max: {{ formatInterval(config.interval_max_seconds) }}
                          </span>
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="d-flex flex-column align-items-center">
                          <label class="toggle-switch mb-1">
                            <input 
                              type="checkbox" 
                              :checked="config.enabled" 
                              @change="toggleWorkerStatus(config, $event)" 
                              :disabled="togglingId === config.id"
                            >
                            <span class="slider"></span>
                          </label>
                          <span class="small" :class="config.enabled ? 'text-success' : 'text-danger'">
                            {{ config.enabled ? 'Enabled' : 'Disabled' }}
                          </span>
                        </div>
                      </td>
                      <td class="text-center action-buttons text-nowrap">
                        <button class="btn btn-sm btn-outline-primary" @click="openEditModal(config)" data-tooltip="Edit Configuration">
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(config)" data-tooltip="Delete Configuration">
                          <i class="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div class="d-flex justify-content-between align-items-center mt-4">
                <div class="text-muted small">
                  Showing {{ startIndex + 1 }} to {{ endIndex }} of {{ filteredConfigs.length }} entries
                </div>
                <nav v-if="totalPages > 1">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click="currentPage--"><i class="bi bi-chevron-left"></i></button>
                    </li>
                    <li v-for="page in totalPages" :key="page" class="page-item" :class="{ active: currentPage === page }">
                      <button class="page-link" @click="currentPage = page">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click="currentPage++"><i class="bi bi-chevron-right"></i></button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Modal Create/Edit -->
    <div class="modal fade" id="workerModal" tabindex="-1" aria-hidden="true" ref="workerModal" data-bs-backdrop="static">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content border-0">
          <div class="modal-header d-flex align-items-center justify-content-between p-3 px-5">
            <h5 class="modal-google-title d-flex align-items-center">
              <div class="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                <i class="bi bi-cpu-fill" :style="{ color: themeColor }"></i>
              </div>
              {{ isEditing ? 'Edit Config' : 'Initialize Unit' }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="saveConfig" autocomplete="off">
            <div class="modal-body p-0">
              <div class="row g-0">
                <!-- Left Column: Core Infrastructure -->
                <div class="col-lg-6 modal-split-divider pt-3 px-5 pb-4">
                  <div class="section-header-google" :style="{ color: themeColor }">
                    <i class="bi bi-gear-wide-connected"></i> Infrastructure Parameters
                  </div>
                  
                  <div class="row g-3">
                    <div class="col-12">
                      <div class="form-floating mb-1">
                        <input type="text" class="form-control" id="workerName" v-model="formData.worker_name" placeholder="Name" required style="color: #000; font-weight: 600;" autocomplete="off">
                        <label for="workerName">Worker Machine Name</label>
                      </div>
                      <div class="google-note">
                        <i class="bi bi-info-circle-fill"></i>
                        Identify this unit in your engine cluster.
                      </div>
                    </div>

                    <div class="col-12">
                      <div class="mb-2">
                        <label for="circle" class="form-label fw-bold text-dark mb-2" style="font-size: 14px;">
                          Circle Instances
                        </label>
                        <select 
                          class="form-select" 
                          id="circle" 
                          v-model="formData.circle" 
                          required
                          style="border: 2px solid #e0e7ff; border-radius: 8px; padding: 10px 14px; font-weight: 500; color: #6b7280; font-size: 14px;"
                        >
                          <option value="" disabled>Choose Circle Instance</option>
                          <option v-for="circle in circles" :key="circle" :value="circle">{{ circle }}</option>
                        </select>
                        <div class="mt-2" style="font-size: 12px; color: #6b7280; line-height: 1.4;">
                          Must be a circle registered in the Instances module.
                        </div>
                      </div>
                    </div>



                    <div class="col-12">
                      <div class="mb-2">
                        <label class="form-label fw-bold text-dark mb-2" style="font-size: 14px;">
                          Destination App(s)
                        </label>
                        
                        <!-- Dropdown Selector -->
                        <select 
                          class="form-select" 
                          id="application" 
                          @change="addApplication($event)"
                          :value="''"
                          style="border: 2px solid #e0e7ff; border-radius: 8px; padding: 10px 14px; font-weight: 500; color: #6b7280; font-size: 14px;"
                        >
                          <option value="" disabled>{{ selectedApplications.length > 0 ? 'Add more...' : 'Choose App(s)' }}</option>
                          <option value="*" style="font-weight: 600; color: #3b82f6;">* (All Applications)</option>
                          <option 
                            v-for="app in availableApplications" 
                            :key="app" 
                            :value="app"
                          >
                            {{ app }}
                          </option>
                        </select>
                        
                        <div class="mt-2" style="font-size: 12px; color: #6b7280; line-height: 1.4;">
                          Select multiple apps or <code style="background: #f3f4f6; padding: 2px 6px; border-radius: 4px; color: #3b82f6; font-size: 11px;">*</code> for all. Click badge to remove.
                        </div>

                        <!-- Selected Applications Tags -->
                        <div v-if="selectedApplications.length > 0" class="mt-3 d-flex gap-2 flex-wrap">
                          <span 
                            v-for="(app, index) in selectedApplications" 
                            :key="index"
                            class="badge rounded-pill d-inline-flex align-items-center gap-1" 
                            :style="{ 
                              ...getBadgeStyle(app), 
                              cursor: 'pointer',
                              padding: '8px 12px',
                              fontSize: '13px',
                              fontWeight: '500'
                            }"
                            @click="removeApplication(app)"
                          >
                            {{ app }}
                            <i class="bi bi-x-circle" style="font-size: 14px;"></i>
                          </span>
                        </div>
                      </div>
                    </div>



                    <div class="col-12">
                      <div class="form-floating mb-1">
                        <select class="form-select fw-bold" id="messageType" v-model="formData.message_type" required>
                          <option value="direct">Direct Message (User)</option>
                          <option value="group">Group Broadcast (Community)</option>
                        </select>
                        <label for="messageType">Processing Type</label>
                      </div>
                      <div class="google-note">
                        Destination outbox table must match type: JID for Direct or Group ID for Group messages.
                      </div>
                    </div>



                    <!-- Interval Range Module -->
                    <div class="col-12 mt-3">
                      <div class="section-header-google mb-2" :style="{ color: themeColor }">
                        <i class="bi bi-clock-history"></i> Execution Range
                      </div>

                      <div class="row g-3">
                        <!-- Minimum Interval -->
                        <div class="col-md-6">
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <label class="insight-stat-label">Minimum Delay</label>
                            <span class="badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill fw-bold" :style="{ color: themeColor }">
                              {{ formatInterval(formData.interval_seconds) }}
                            </span>
                          </div>
                          <input 
                            type="range" 
                            class="google-range-slider" 
                            v-model.number="sliderMinValue" 
                            min="0" 
                            max="7200" 
                            step="1"
                            @input="updateIntervalsFromSliders('min')"
                            :style="{ '--primary-color': themeColor }"
                          >
                        </div>

                        <!-- Maximum Interval -->
                        <div class="col-md-6">
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <label class="insight-stat-label">Maximum Delay</label>
                            <span class="badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill fw-bold" :style="{ color: themeColor }">
                              {{ formatInterval(formData.interval_max_seconds) }}
                            </span>
                          </div>
                          <input 
                            type="range" 
                            class="google-range-slider" 
                            v-model.number="sliderMaxValue" 
                            min="0" 
                            max="7200" 
                            step="1"
                            @input="updateIntervalsFromSliders('max')"
                            :style="{ '--primary-color': themeColor }"
                          >
                        </div>
                      </div>

                      <div class="google-note mt-4">
                        <i class="bi bi-shuffle"></i>
                        Engine will pick a <b>random delay</b> between min and max for each cycle.
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Right Column: Live Engine Insight -->
                <div class="col-lg-6 pt-3 px-5 pb-4 bg-light bg-opacity-50">
                  <div class="section-header-google" :style="{ color: themeColor }">
                    <i class="bi bi-activity"></i> Live Engine Insight
                  </div>

                  <div class="engine-insight-card mb-4">
                    <div class="row align-items-center">
                      <div class="col-7">
                        <div class="insight-stat-label mb-2">Intensity Level</div>
                        <div class="d-flex align-items-baseline">
                          <span class="insight-stat-value">{{ calculateIntensity() }}%</span>
                          <span :class="intensityClass" class="small fw-bold ms-2">{{ intensityLabel }}</span>
                        </div>
                      </div>
                      <div class="col-5 text-end">
                        <div class="status-pulse-container justify-content-end">
                          <span class="pulse-dot"></span>
                          <span class="small fw-bold text-success">Live Engine</span>
                        </div>
                      </div>
                    </div>
                    <div class="mt-3 border-top pt-2">
                      <p class="mb-0 text-muted" style="font-size: 0.65rem; line-height: 1.3;">
                        <i class="bi bi-info-circle me-1"></i>
                        <b>Live Insight</b> memprediksi beban sistem. Semakin kecil interval, semakin tinggi intensitas pengolahan data.
                      </p>
                    </div>
                  </div>

                  <!-- Panduan Info Webhook Worker -->
                  <div class="alert alert-info border-0 rounded-4 p-3 mb-3" style="background-color: #f0f7ff; border-left: 4px solid #0d6efd !important;">
                    <div class="d-flex align-items-start">
                      <i class="bi bi-info-circle-fill text-primary fs-5 me-2 mt-1"></i>
                      <div>
                        <div class="fw-bold text-primary small mb-1">📘 Webhook Menu "Worker / Outbox" (Delivery Callback):</div>
                        <ul class="ps-3 mb-1 text-dark" style="font-size: 0.75rem; line-height: 1.4;">
                          <li><b>Fungsi:</b> Menerima Laporan Status Pengiriman Pesan (Delivery Callback).</li>
                          <li><b>Cara Kerja:</b> Setiap kali sistem selesai mengirimkan pesan antrian blast/outbox, notifikasi status (SUKSES / GAGAL) dikirimkan ke URL ini.</li>
                          <li><b>Cocok untuk:</b> Update status invoice otomatis, reporting broadcast, tracking pesan gagal.</li>
                        </ul>
                        <div class="text-secondary" style="font-size: 0.72rem;">
                          💡 <i>Gunakan Webhook Menu "Instance" jika ingin memproses pesan masuk dari pelanggan (Incoming Messages).</i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="row g-3">
                    <div class="col-12">
                      <div class="form-floating mb-1">
                        <input type="text" class="form-control" id="webhookUrl" v-model="formData.webhook_url" placeholder="URL" autocomplete="off">
                        <label for="webhookUrl">Callback Webhook (Hookup)</label>
                      </div>
                      <div class="google-note">
                        <i class="bi bi-link-45deg"></i>
                        External API endpoint untuk laporan status pengiriman pesan antrian.
                      </div>
                    </div>

                    <div class="col-12">
                      <div class="form-floating mb-1">
                        <input type="password" class="form-control" id="webhookSecret" v-model="formData.webhook_secret" placeholder="Secret" autocomplete="new-password">
                        <label for="webhookSecret">Automation Token (Secret)</label>
                      </div>
                      <div class="google-note">
                        <i class="bi bi-shield-check"></i>
                        Security key target for: <code>hash_hmac('sha256', $raw_payload, $secret)</code>.
                      </div>
                    </div>



                    <!-- Media Processing Module -->
                    <div class="col-12">
                      <div class="p-3 rounded-4 bg-white border shadow-sm transition-all" :style="{ borderLeft: `5px solid ${formData.allow_media ? themeColor : '#dee2e6'}` }">
                        <div class="d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center">
                            <div class="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                              <i class="bi bi-images" :style="{ color: themeColor }"></i>
                            </div>
                            <div>
                              <h6 class="mb-0 fw-bold text-dark small">Media Processing</h6>
                              <p class="mb-0 text-muted" style="font-size: 0.65rem;">Allow engine to process & send media attachments.</p>
                            </div>
                          </div>
                          <div class="form-check form-switch m-0">
                            <input class="form-check-input" type="checkbox" id="mediaSwitch" v-model="formData.allow_media" style="height: 1.5rem; width: 3rem; cursor: pointer;">
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Auto-Cancel Pending Outbox Module (replace_pending) -->
                    <div class="col-12">
                      <div class="p-3 rounded-4 bg-white border shadow-sm transition-all" :style="{ borderLeft: `5px solid ${formData.replace_pending ? '#ffc107' : '#dee2e6'}` }">
                        <div class="d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center">
                            <div class="bg-warning bg-opacity-10 p-2 rounded-3 me-3">
                              <i class="bi bi-arrow-counterclockwise text-warning fs-5"></i>
                            </div>
                            <div>
                              <h6 class="mb-0 fw-bold text-dark small">Auto-Cancel Pesan Pending Lama</h6>
                              <p class="mb-0 text-muted" style="font-size: 0.65rem;">Membatalkan otomatis pesan pending lama jika ada request pesan baru ke nomor yang sama.</p>
                            </div>
                          </div>
                          <div class="form-check form-switch m-0">
                            <input class="form-check-input" type="checkbox" id="replacePendingSwitch" v-model="formData.replace_pending" style="height: 1.5rem; width: 3rem; cursor: pointer;">
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Activation Module -->
                    <div class="col-12 mt-3">
                      <div class="p-4 rounded-4 bg-white border shadow-sm transition-all" :style="{ borderLeft: `5px solid ${formData.enabled ? themeColor : '#ccc'}` }">
                        <div class="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 class="mb-1 fw-extrabold text-dark small">Engine Status</h6>
                            <p class="mb-0 text-muted" style="font-size: 0.7rem;">{{ formData.enabled ? 'Unit will begin processing upon commit.' : 'Unit will remain dormant.' }}</p>
                          </div>
                          <div class="form-check form-switch m-0">
                            <input class="form-check-input" type="checkbox" id="enabledSwitch" v-model="formData.enabled" style="height: 1.8rem; width: 3.6rem; cursor: pointer;">
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer p-3 px-5 bg-white border-top">
              <button type="button" class="btn btn-google-light" data-bs-dismiss="modal">Dismiss</button>
              <button 
                type="submit" 
                class="btn btn-google-primary text-white scale-hover" 
                :disabled="saving"
                :style="{ backgroundColor: themeColor }"
              >
                <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <i v-else class="bi bi-cpu me-2"></i>
                {{ isEditing ? 'Commit Configuration' : 'Deploy Engine' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { workerService } from '@/services/workerService';
import { useSystem } from '@/composables/useSystem';
import lottie from 'lottie-web';

export default {
  name: 'WorkerConfig',
  setup() {
    const { themeColor } = useSystem();
    return { themeColor };
  },
  data() {
    return {
      configs: [],
      circles: [],
      applications: [],
      loading: false,
      saving: false,
      togglingId: null,
      searchQuery: '',
      currentPage: 1,
      entriesPerPage: 10,
      isEditing: false,
      sliderMinValue: 0,
      sliderMaxValue: 0,
      formData: {
        id: null,
        worker_name: '',
        circle: '',
        application: '',
        message_type: 'direct',
        interval_seconds: 5,
        interval_max_seconds: 15,
        enabled: true,
        allow_media: false,
        replace_pending: false,
        webhook_url: '',
        webhook_secret: ''
      },
      selectedApplications: [], // Array untuk multi-select
      modalInstance: null
    };
  },
  computed: {
    filteredConfigs() {
      if (!this.searchQuery) return this.configs;
      const query = this.searchQuery.toLowerCase();
      return this.configs.filter(c => 
        c.worker_name.toLowerCase().includes(query) || 
        c.circle.toLowerCase().includes(query) ||
        c.application.toLowerCase().includes(query)
      );
    },
    paginatedConfigs() {
      const start = (this.currentPage - 1) * this.entriesPerPage;
      return this.filteredConfigs.slice(start, start + this.entriesPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredConfigs.length / this.entriesPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.entriesPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.entriesPerPage, this.filteredConfigs.length);
    },
    intensityClass() {
      const intensity = this.calculateIntensity();
      if (intensity > 70) return 'text-danger';
      if (intensity > 30) return 'text-warning';
      return 'text-success';
    },
    intensityLabel() {
      const intensity = this.calculateIntensity();
      if (intensity > 70) return 'Heavy Load';
      if (intensity > 30) return 'Standard';
      return 'Optimized';
    },
    availableApplications() {
      // Filter out already selected applications
      return this.applications.filter(app => !this.selectedApplications.includes(app));
    }
  },
  mounted() {
    if (window.bootstrap) {
      this.initModal();
    } else {
      window.addEventListener('bootstrap-ready', this.initModal);
    }
    
    this.fetchConfigs();
    this.fetchDropdownData();
  },
  beforeUnmount() {
    window.removeEventListener('bootstrap-ready', this.initModal);
    if (this.modalInstance) {
      this.modalInstance.hide();
    }
  },
  methods: {
    initModal() {
      if (this.$refs.workerModal) {
        this.modalInstance = new window.bootstrap.Modal(this.$refs.workerModal);
      }
    },
    async fetchConfigs() {
      this.loading = true;
      try {
        const res = await workerService.getConfigs();
        if (res.success) {
          this.configs = res.data || [];
        }
      } catch (error) {
        this.showToast('Failed to fetch configurations', 'error');
      } finally {
        this.loading = false;
      }
    },
    async fetchDropdownData() {
      try {
        const [circleRes, appRes] = await Promise.all([
          workerService.getCircles(),
          workerService.getApplications()
        ]);
        if (circleRes.success) this.circles = circleRes.data || [];
        if (appRes.success) this.applications = appRes.data || [];
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    },
    openCreateModal() {
      this.isEditing = false;
      this.formData = {
        id: null,
        worker_name: '',
        circle: '',
        application: '',
        message_type: 'direct',
        interval_seconds: 5,
        interval_max_seconds: 15,
        enabled: true,
        allow_media: false,
        replace_pending: false,
        webhook_url: '',
        webhook_secret: ''
      };
      this.selectedApplications = []; // Reset selected apps
      this.syncSlidersFromIntervals();
      this.modalInstance.show();
    },
    getNullString(val) {
      if (!val) return '';
      if (typeof val === 'string') return val;
      if (typeof val === 'object') {
        if (val.Valid === false) return '';
        if (val.String !== undefined && val.String !== null) return val.String;
      }
      return '';
    },
    openEditModal(config) {
      this.isEditing = true;
      this.formData = { 
        ...config,
        allow_media: !!config.allow_media,
        replace_pending: !!config.replace_pending,
        webhook_url: this.getNullString(config.webhook_url),
        webhook_secret: this.getNullString(config.webhook_secret)
      };
      // Parse existing application string to array
      this.selectedApplications = this.parseApplications(config.application);
      this.syncSlidersFromIntervals();
      this.modalInstance.show();
    },
    async saveConfig() {
      this.saving = true;
      
      // Convert selectedApplications array to comma-separated string
      this.formData.application = this.selectedApplications.join(', ');
      
      try {
        let res;
        if (this.isEditing) {
          res = await workerService.updateConfig(this.formData.id, this.formData);
        } else {
          res = await workerService.createConfig(this.formData);
        }

        if (res.success) {
          this.showToast(res.message || 'Saved successfully', 'success');
          this.modalInstance.hide();
          await this.fetchConfigs();
        } else {
          this.showToast(res.message || 'Failed to save', 'error');
        }
      } catch (error) {
        this.showToast('An error occurred while saving', 'error');
      } finally {
        this.saving = false;
      }
    },
    async toggleWorkerStatus(config, event) {
      const originalValue = config.enabled;
      const action = originalValue ? 'Disable' : 'Enable';
      
      const result = await this.$swal.fire({
        title: 'Confirm Status Change',
        text: `Are you sure you want to ${action.toLowerCase()} worker "${config.worker_name}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: `Yes, ${action} it!`,
        cancelButtonText: 'Cancel',
        confirmButtonColor: originalValue ? '#dc3545' : '#28a745',
      });

      if (!result.isConfirmed) {
        // Revert the checkbox state in the DOM
        if (event && event.target) {
          event.target.checked = originalValue;
        }
        return;
      }

      this.togglingId = config.id;
      try {
        const res = await workerService.toggleConfig(config.id);
        if (res.success) {
          config.enabled = !originalValue;
          this.showToast(res.message || 'Status updated', 'success');
        } else {
          this.showToast(res.message || 'Failed to update status', 'error');
        }
      } catch (error) {
        this.showToast('Network error while toggling status', 'error');
      } finally {
        this.togglingId = null;
      }
    },
    async confirmDelete(config) {
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: `You are about to delete worker "${config.worker_name}". This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Delete it!',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
      });

      if (result.isConfirmed) {
        try {
          const res = await workerService.deleteConfig(config.id);
          if (res.success) {
            this.showToast(res.message || 'Deleted successfully', 'success');
            await this.fetchConfigs();
          } else {
            this.showToast(res.message || 'Failed to delete', 'error');
          }
        } catch (error) {
          this.showToast('An error occurred during deletion', 'error');
        }
      }
    },
    showToast(message, icon = 'info') {
      this.$swal.fire({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        icon: icon,
        title: message
      });
    },
    getBadgeStyle(text) {
      if (!text) return {};
      
      // Palet warna: Background Lembut (soft), Text & Border Pekat (doop)
      // Meniru style 'bg-type-direct' yang memiliki kontras tinggi namun bg kalem
      const colors = [
        { bg: '#e7f5ff', text: '#1864ab', border: '#abc9fb' }, // Blue
        { bg: '#ebfbee', text: '#2b8a3e', border: '#b2f2bb' }, // Green
        { bg: '#f3f0ff', text: '#6741d9', border: '#d1c4e9' }, // Purple
        { bg: '#eef2ff', text: '#4338ca', border: '#c7d2fe' }, // Indigo
        { bg: '#f1f5f9', text: '#334155', border: '#cbd5e1' }, // Slate/Steel
        { bg: '#e6fcf5', text: '#087f5b', border: '#c3fae8' }, // Teal
        { bg: '#f0f9ff', text: '#0369a1', border: '#bae6fd' }, // Sky/Deep Blue
        { bg: '#f8fafc', text: '#1e293b', border: '#e2e8f0' }, // Slate Blue
        { bg: '#fdf4ff', text: '#701a75', border: '#f5d0fe' }, // Deep Purple/Plum
        { bg: '#ecfeff', text: '#155e75', border: '#a5f3fc' }, // Cyan/Ocean
      ];
      
      // Hash sederhana untuk menentukan warna berdasarkan teks
      let hash = 0;
      const seed = text.toLowerCase().trim();
      for (let i = 0; i < seed.length; i++) {
        hash = seed.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash) % colors.length;
      const color = colors[index];
      
      return {
        backgroundColor: color.bg,
        color: color.text,
        border: `1px solid ${color.border}`,
        padding: '6px 12px',
        fontWeight: '700',
        fontSize: '10px',
        minWidth: '85px',
        display: 'inline-block'
      };
    },
    parseApplications(applicationString) {
      if (!applicationString) return [];
      if (applicationString.trim() === '*') return ['*'];
      return applicationString.split(',').map(app => app.trim()).filter(app => app);
    },
    addApplication(event) {
      const value = event.target.value;
      if (!value) return;
      
      // If wildcard is selected, clear all and set only wildcard
      if (value === '*') {
        this.selectedApplications = ['*'];
      } else if (!this.selectedApplications.includes('*')) {
        // Only add if wildcard is not already selected
        if (!this.selectedApplications.includes(value)) {
          this.selectedApplications.push(value);
        }
      }
      
      // Reset dropdown
      event.target.value = '';
    },
    removeApplication(app) {
      const index = this.selectedApplications.indexOf(app);
      if (index > -1) {
        this.selectedApplications.splice(index, 1);
      }
    },
    isWebhookActive(config) {
      if (!config.webhook_url) return false;
      // Handle Go sql.NullString structure
      if (typeof config.webhook_url === 'object') {
        return config.webhook_url.Valid && config.webhook_url.String && config.webhook_url.String !== '';
      }
      // Handle plain string fallback
      return config.webhook_url && config.webhook_url !== '';
    },
    formatInterval(seconds) {
      if (seconds < 60) return `${seconds}s`;
      if (seconds < 3600) {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return secs > 0 ? `${mins}m ${secs}s` : `${mins} Minutes`;
      }
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return mins > 0 ? `${hrs}h ${mins}m` : `${hrs} Hours`;
    },
    calculateIntensity() {
      // Calculate based on the average of min and max
      const avg = (this.formData.interval_seconds + this.formData.interval_max_seconds) / 2;
      const val = Math.max(1, avg);
      if (val <= 60) return 100 - Math.floor((val - 1) * 0.5); 
      if (val <= 3600) return 70 - Math.floor((val - 60) / 60); 
      return Math.max(1, 10 - Math.floor((val - 3600) / 720)); 
    },
    updateIntervalsFromSliders(source) {
      const mapSliderToSeconds = (val) => {
        if (val <= 3600) {
          return Math.round(1 + (val / 3600) * (1800 - 1));
        } else {
          return Math.round(1800 + ((val - 3600) / 3600) * (7200 - 1800));
        }
      };

      if (source === 'min') {
        this.formData.interval_seconds = mapSliderToSeconds(this.sliderMinValue);
        if (this.formData.interval_seconds > this.formData.interval_max_seconds) {
          this.formData.interval_max_seconds = this.formData.interval_seconds;
          this.syncSlidersFromIntervals('max');
        }
      } else {
        this.formData.interval_max_seconds = mapSliderToSeconds(this.sliderMaxValue);
        if (this.formData.interval_max_seconds < this.formData.interval_seconds) {
          this.formData.interval_seconds = this.formData.interval_max_seconds;
          this.syncSlidersFromIntervals('min');
        }
      }
    },
    syncSlidersFromIntervals(target = 'both') {
      const mapSecondsToSlider = (val) => {
        if (val <= 1800) {
          return Math.round(((val - 1) / (1800 - 1)) * 3600);
        } else {
          return Math.round(3600 + ((val - 1800) / (7200 - 1800)) * 3600);
        }
      };

      if (target === 'both' || target === 'min') {
        this.sliderMinValue = mapSecondsToSlider(this.formData.interval_seconds);
      }
      if (target === 'both' || target === 'max') {
        this.sliderMaxValue = mapSecondsToSlider(this.formData.interval_max_seconds);
      }
    }
  }
};
</script>

<style scoped>
@import '@/../public/assets/css/worker-theme.css';

.bg-info-light { background-color: rgba(13, 202, 240, 0.1); }
.bg-primary-light { background-color: rgba(13, 110, 253, 0.1); }
.bg-secondary-light { background-color: rgba(108, 117, 125, 0.1); }

.fade-in {
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
